import React, {useState, useEffect, useCallback} from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon as RefreshIcon } from '@heroicons/react/24/outline';
import {getDirectionHistory, getDirections, getOngoingTrade} from "../../services/botService";


const TradingData = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [loadingTrade, setLoadingTrade] = useState(true);
  const [directionHistory, setDirectionHistory] = useState([]);
  const [recentDirections, setRecentDirections] = useState([]);
  const [ongoingTrade, setOngoingTrade] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  const refreshInterval = 30000; // 30 seconds

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get direction icon
  const getDirectionIcon = (direction) => {
    return direction === 'UP' ? (
      <ArrowUpIcon className="h-5 w-5" />
    ) : (
      <ArrowDownIcon className="h-5 w-5" />
    );
  };

  // Get direction color class
  const getDirectionColor = (direction) => {
    return direction === 'UP' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const fetchDirectionHistory = useCallback(async () => {
    try {
      setLoading(true);
      
      const { success, directionHistory = [], total = 0, message } = await getDirectionHistory(currentPage, itemsPerPage);
      
      if (success) {
        // Ensure we have valid direction data
        const validItems = directionHistory.filter(item => item && item.direction);
        
        setDirectionHistory(validItems);
        setTotalItems(parseInt(total, 10) || 0);
      } else {
        // console.error('Failed to fetch direction history:', message);
        setDirectionHistory([]);
        setTotalItems(0);
      }
    } catch (error) {
      // console.error('Error fetching direction history:', error);
      // console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  // Fetch direction history with pagination
  useEffect(() => {
    fetchDirectionHistory();
  }, [fetchDirectionHistory]);

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  
  // Fetch recent directions
  const fetchRecentDirections = useCallback(async () => {
    try {
      setLoadingRecent(true);
      const response = await getDirections();
      if (response && Array.isArray(response)) {
        setRecentDirections(response);
      } else if (response && response.directions) {
        setRecentDirections(response.directions);
      }
    } catch (error) {
      // console.error('Error fetching recent directions:', error);
    } finally {
      setLoadingRecent(false);
    }
  }, []);
  
  // Fetch ongoing trade status
  const fetchOngoingTrade = useCallback(async () => {
    try {
      setLoadingTrade(true);
      const response = await getOngoingTrade();
      
      if (response && response.success && response.data && response.data.direction) {
        setOngoingTrade(response.data);
      } else {
        setOngoingTrade(null);
      }
    } catch (error) {
      // console.error('Error fetching ongoing trade:', error);
      setOngoingTrade(null);
    } finally {
      setLoadingTrade(false);
    }
  }, []);

  // Set up auto-refresh for all data
  useEffect(() => {
    // Initial fetches
    fetchRecentDirections();
    fetchOngoingTrade();
    
    // Set up interval for auto-refresh
    const intervalId = setInterval(() => {
      fetchDirectionHistory();
      fetchRecentDirections();
      fetchOngoingTrade();
    }, refreshInterval);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [fetchRecentDirections, fetchDirectionHistory, fetchOngoingTrade]);

  // Function to refresh all data
  const refreshAllData = useCallback(async () => {
    setLoading(true);
    setLoadingRecent(true);
    setLoadingTrade(true);

    try {
      await Promise.all([
        fetchDirectionHistory(),
        fetchRecentDirections(),
        fetchOngoingTrade()
      ]);
    } catch (error) {
      // console.error('Error refreshing data:', error);
    } finally {
      setLoading(false);
      setLoadingRecent(false);
      setLoadingTrade(false);
    }
  }, [fetchDirectionHistory, fetchRecentDirections, fetchOngoingTrade]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Trading Data</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View your trading activity and history.
          </p>
        </div>
        <button
          onClick={refreshAllData}
          disabled={loading || loadingRecent || loadingTrade}
          className="p-2 rounded-full text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
          title="Refresh all data"
        >
          <RefreshIcon className={`h-5 w-5 ${(loading || loadingRecent || loadingTrade) ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Current Trade */}
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg mb-6 transition-colors duration-200">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Current Trade</h3>
              <button
                onClick={fetchOngoingTrade}
                disabled={loadingTrade}
                className="p-1 rounded-full text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
                title="Refresh trade status"
              >
                <RefreshIcon className={`h-5 w-5 ${loadingTrade ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            {loadingTrade ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : ongoingTrade ? (
              <div className="mt-4">
                <div className="flex items-center">
                  <span className={`px-3 py-1 inline-flex items-center text-sm font-medium rounded-full ${
                    ongoingTrade.direction === 'UP' 
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  }`}>
                    {ongoingTrade.direction}
                  </span>
                  <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                    Placed at: {ongoingTrade.time_placement}
                  </span>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Active trade in progress
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No active trades</p>
            )}
          </div>
        </div>

        {/* Recent Directions */}
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg mb-6 transition-colors duration-200">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Recent Directions</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                  Latest trading directions (last 5)
                </p>
              </div>
              <button
                onClick={fetchRecentDirections}
                disabled={loadingRecent}
                className="p-1 rounded-full text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
                title="Refresh recent directions"
              >
                <RefreshIcon className={`h-5 w-5 ${loadingRecent ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          <div className="px-4 py-5">
            {loadingRecent ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : recentDirections.length > 0 ? (
              <div className="grid grid-cols-5 gap-4">
                {recentDirections.map((item, index) => (
                  <div 
                    key={`${item.id || index}-${item.time_placement || ''}`} 
                    className="flex flex-col items-center"
                  >
                    <div className={`p-2 rounded-full ${
                      item.direction === 'UP' 
                        ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300' 
                        : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300'
                    }`}>
                      {getDirectionIcon(item.direction)}
                    </div>
                    {item.time_placement && (
                      <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {new Date(item.time_placement).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No direction data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Direction History */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg transition-colors duration-200">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Direction History</h3>
            <button
              onClick={() => {
                setCurrentPage(1);
                fetchDirectionHistory();
              }}
              className="p-1 rounded-full text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              title="Refresh data"
              disabled={loading}
            >
              <RefreshIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            Complete history of trading directions
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Direction
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {directionHistory.length > 0 ? (
                directionHistory.map((item, index) => (
                  <tr key={`${item.id || index}-${item.time_placement || ''}`} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.time_placement ? formatDate(item.time_placement) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.direction === 'UP'
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }`}>
                        {item.direction}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    <p>No direction history available</p>
                    <p className="text-xs mt-1 text-gray-400">
                      {totalItems > 0 ? 'Try changing the page' : 'Start trading to see history'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:justify-end">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex items-center mx-4">
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">
                      {directionHistory.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                    </span> to {' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, totalItems)}
                    </span>{' '}
                    of <span className="font-medium">{totalItems}</span> results
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingData;
